import { type PricingPlan, PricingSection } from "@/components/Plans/Plans";
import { Loading } from "@/components/ui";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { BillingAlertStatus, GatewayType } from "@/constants";
import { useAuth } from "@/contexts/AuthContext";
import { useAPI } from "@/hooks/useAPI";
import { useBillingStatus } from "@/hooks/useBillingStatus";
import { invalidateCreditBalanceCache } from "@/hooks/useCreditBalance";
import { invalidateSubscriptionCache } from "@/hooks/useSubscriptionStatus";
import {
  useCanCreateBillingPortal,
  useCanReadSubscriptions,
  usePermissionsV2Loaded,
} from "@/permissions/v2";
import { SubscriptionService } from "@/services/subscriptionService";
import { getErrorMessage } from "@/utils/errorUtils";
import { buildTeamMailtoHref } from "@/utils/waitlistContact";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { useSearchParams } from "react-router-dom";

import { BillingAccessBanner } from "./BillingAccessBanner";
import { BillingAccessRestrictedView } from "./BillingAccessRestrictedView";
import { BannerState, SubscriptionBanner } from "./SubscriptionBanner";
import { UpdatePaymentMethodBanner } from "./UpdatePaymentMethodBanner";

type PlanChangeAction = "upgrade" | "downgrade";

const POLLING_INTERVAL = 5 * 1000; // 5 seconds
const MAX_POLL_COUNT = 60;

export const SubscriptionPage = () => {
  const [loadingPlanId, setLoadingPlanId] = useState<string | null>(null);
  const [activePriceProductId, setActivePriceProductId] = useState<string | null>(null);
  const [isFreeTrial, setIsFreeTrial] = useState(false);
  const [isLoadingActiveSubscription, setIsLoadingActiveSubscription] = useState(true);
  const [isLoadingManageSubscription, setIsLoadingManageSubscription] = useState(false);

  const [bannerState, setBannerState] = useState<BannerState | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [planChangeModal, setPlanChangeModal] = useState<{
    plan: PricingPlan;
    action: PlanChangeAction;
  } | null>(null);
  const [isConfirmingPlanChange, setIsConfirmingPlanChange] = useState(false);

  const { firmContext, user } = useAuth();
  const enterpriseContactHref = user ? buildTeamMailtoHref() : undefined;
  const { billingStatus } = useBillingStatus();
  const isPermissionsLoaded = usePermissionsV2Loaded();
  const canViewSubscriptions = useCanReadSubscriptions(firmContext?.firmId);
  const canManageBilling = useCanCreateBillingPortal(firmContext?.firmId);

  const v2Api = useAPI("v2");
  const subscriptionService = useMemo(() => new SubscriptionService(v2Api), [v2Api]);

  const hasPendingInvoice =
    billingStatus?.status !== BillingAlertStatus.CURRENT && billingStatus?.status !== undefined;

  const fetchActiveSubscription = async () => {
    if (!firmContext?.firmId) return null;

    try {
      const response = await subscriptionService.getActiveSubscription(firmContext.firmId);
      setActivePriceProductId(
        response.has_active_subscription && response.product_price_id && !response.is_free_trial
          ? response.product_price_id
          : null
      );
      setIsFreeTrial(response.is_free_trial);
      return response;
    } catch {
      toast.error("Failed to fetch active subscription.");
      setActivePriceProductId(null);
      setIsFreeTrial(false);
      return null;
    }
  };

  const handleQueryParams = () => {
    const successParam = searchParams.get("success");

    if (successParam === "false") {
      setBannerState(BannerState.CANCEL);
    } else if (successParam === "true") {
      setBannerState(BannerState.PROCESSING);
    }

    if (successParam) {
      searchParams.delete("success");
      setSearchParams(searchParams, { replace: true });
    }
  };

  const pollSubscriptionStatus = async () => {
    try {
      const response = await fetchActiveSubscription();
      if (!response) return;

      if (response.has_active_subscription && response.product_price_id) {
        invalidateSubscriptionCache();
        invalidateCreditBalanceCache();
        setBannerState(BannerState.SUCCESS);
      }
    } catch {
      // Do nothing
    }
  };

  useEffect(() => {
    // Only poll when banner is in PROCESSING state and we have firmId
    if (bannerState !== BannerState.PROCESSING || !firmContext?.firmId) {
      return;
    }

    let pollCount = 0;
    const pollInterval = setInterval(async () => {
      pollCount++;
      if (pollCount > MAX_POLL_COUNT) {
        setBannerState(null);
        return;
      }
      pollSubscriptionStatus();
    }, POLLING_INTERVAL);

    return () => clearInterval(pollInterval);
  }, [bannerState, firmContext?.firmId]);

  useEffect(() => {
    if (!firmContext?.firmId) {
      setIsLoadingActiveSubscription(false);
      return;
    }

    const initialize = async () => {
      const subscriptionResponse = await fetchActiveSubscription();
      setIsLoadingActiveSubscription(false);
      handleQueryParams();

      if (subscriptionResponse?.has_active_subscription && subscriptionResponse.product_price_id) {
        invalidateSubscriptionCache();
        invalidateCreditBalanceCache();
        if (bannerState !== null) {
          setBannerState(BannerState.SUCCESS);
        }
      }
    };

    initialize();
  }, [firmContext?.firmId, searchParams]);

  const handlePlanSelect = async (
    plan: PricingPlan,
    options?: { action: "subscribe" | "upgrade" | "downgrade" }
  ) => {
    // Special handling for enterprise plans
    if (plan.name.toLowerCase() === "enterprise") {
      window.open(buildTeamMailtoHref());
      return;
    }

    if (!canManageBilling) {
      return;
    }

    if (!firmContext?.firmId) {
      toast.error("Firm context not available.");
      return;
    }

    if (hasPendingInvoice) {
      toast.error("You have a pending invoice. Please resolve it before making a new purchase.");
      return;
    }

    if (activePriceProductId === plan.pricingId) {
      toast.error("You're already on this plan.");
      return;
    }

    const isUpgradeOrDowngrade = options?.action === "upgrade" || options?.action === "downgrade";

    if (isUpgradeOrDowngrade) {
      setPlanChangeModal({
        plan,
        action: options!.action as PlanChangeAction,
      });
      return;
    }

    if (loadingPlanId) return;
    try {
      setLoadingPlanId(plan.pricingId);
      const response = await subscriptionService.createCheckoutSession(
        firmContext.firmId,
        plan.pricingId
      );
      window.location.href = response.payment_link;
    } catch {
      toast.error("Failed to create checkout session.");
    } finally {
      setLoadingPlanId(null);
    }
  };

  const handleConfirmPlanChange = async () => {
    if (!planChangeModal?.plan || !firmContext?.firmId || !canManageBilling) return;

    setIsConfirmingPlanChange(true);
    try {
      const response = await subscriptionService.updatePlan(
        firmContext.firmId,
        planChangeModal.plan.pricingId
      );
      if (response.success) {
        toast.success("Updating your plan…");
        setPlanChangeModal(null);
        await new Promise((resolve) => setTimeout(resolve, 2000));
        invalidateSubscriptionCache();
        invalidateCreditBalanceCache();
        const updated = await fetchActiveSubscription();
        if (updated?.has_active_subscription && updated.product_price_id) {
          setActivePriceProductId(updated.product_price_id);
          setBannerState(BannerState.SUCCESS_PLAN_CHANGED);
          toast.success("Plan changed successfully.");
        }
      } else {
        toast.error("Failed to change plan.");
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsConfirmingPlanChange(false);
    }
  };

  const handleManageSubscription = async () => {
    if (isLoadingManageSubscription || !canManageBilling) return;

    if (!firmContext?.firmId) {
      toast.error("Firm context not available.");
      return;
    }

    try {
      setIsLoadingManageSubscription(true);
      const response = await subscriptionService.createCustomerPortalSession(
        firmContext.firmId,
        GatewayType.STRIPE
      );
      window.location.href = response.portal_url;
    } catch {
      toast.error("Failed to open subscription management portal.");
      setIsLoadingManageSubscription(false);
    }
  };

  const shouldShowUpdatePaymentCard =
    hasPendingInvoice && activePriceProductId === null && !isLoadingActiveSubscription;

  if (!isPermissionsLoaded) {
    return <Loading fullScreen text="Loading billing access..." />;
  }

  if (!canViewSubscriptions) {
    return <BillingAccessRestrictedView />;
  }

  return (
    <div className="min-h-screen">
      <SubscriptionBanner bannerState={bannerState} onDismiss={() => setBannerState(null)} />
      {!canManageBilling && <BillingAccessBanner />}
      {canManageBilling && shouldShowUpdatePaymentCard && (
        <UpdatePaymentMethodBanner
          onUpdatePaymentMethod={handleManageSubscription}
          isLoading={isLoadingManageSubscription}
        />
      )}
      <ConfirmDialog
        open={planChangeModal !== null}
        onOpenChange={(open) => {
          if (!open) setPlanChangeModal(null);
        }}
        title={planChangeModal?.action === "downgrade" ? "Downgrade plan?" : "Upgrade plan?"}
        description={
          planChangeModal?.action === "downgrade"
            ? `Are you sure you want to downgrade to ${planChangeModal?.plan.name ?? ""}?`
            : `Are you sure you want to upgrade to ${planChangeModal?.plan.name ?? ""}?`
        }
        confirmLabel={planChangeModal?.action === "downgrade" ? "Yes, downgrade" : "Yes, upgrade"}
        onConfirm={handleConfirmPlanChange}
        isConfirming={isConfirmingPlanChange}
      />
      <PricingSection
        heading="Choose Your Plan"
        onPlanSelect={handlePlanSelect}
        loadingPlanId={loadingPlanId}
        showFeatures={true}
        activePriceProductId={activePriceProductId}
        isLoadingActiveSubscription={isLoadingActiveSubscription}
        onManageSubscription={
          canManageBilling && activePriceProductId !== null ? handleManageSubscription : undefined
        }
        isLoadingManageSubscription={isLoadingManageSubscription}
        disabled={hasPendingInvoice || !canManageBilling}
        enterpriseContactHref={enterpriseContactHref}
        isFreeTrial={isFreeTrial}
      />
    </div>
  );
};
